<template>
  <div class="wt-mass-produce">
    <div class="-header">
      <i
        class="-close wt2-close wticon wticon-close wt2-color-normal wt2-text-m wt2-text-lm close-icon"
        :class="{ '-disabled': isProducing }"
        @click="closePrompt"
      />

      <div class="-title wt2-text-l3 wt2-text-light">Mass Video Produce</div>
    </div>

    <div class="-breadcrumbs">
      <div
        :class="{
          '-active': stage === stages.UPLOAD || stage === stages.BROWSE,
          '-enabled': !hasFinished && !isProducing,
        }"
        @click="setStage(stages.BROWSE)"
      >
        <div class="wt2-text-s">Step 1</div>
        <div>Upload File</div>
      </div>
      <div
        :class="{
          '-active': stage === stages.PREVIEW,
          '-enabled': fileValid && !hasFinished && !isProducing,
        }"
        @click="setStage(stages.PREVIEW)"
      >
        <div class="wt2-text-s">Step 2</div>
        <div>Preview Sample</div>
      </div>
      <div
        :class="{
          '-active': stage === stages.CONFIRM,
          '-enabled': fileValid && !hasFinished && !isProducing,
        }"
        @click="setStage(stages.CONFIRM)"
      >
        <div class="wt2-text-s">Step 3</div>
        <div>Confirm</div>
      </div>
      <div :class="{ '-active': stage === stages.FINISH }">
        <div class="wt2-text-s">Step 4</div>
        <div>Finish</div>
      </div>
    </div>

    <div class="-stage">
      <div class="-spinner" v-if="showSpinner">
        <div class="wt-loader"></div>
      </div>

      <massProduceUpload
        v-if="stage === stages.UPLOAD"
        :onFileChange="onFileChange"
        :clearFileError="clearFileError"
        :fileError="fileError"
        :acceptedMimes="acceptedMimes"
        :storyboard="storyboard"
        :errors="errors"
        :linkedFields="linkedFields"
      />

      <massProduceBrowse
        v-if="fileKey"
        v-show="stage === stages.BROWSE"
        :key="fileKey"
        :onFileChange="onFileChange"
        :fileData="fileData"
        :previewRowIndex="previewRowIndex"
        :setPreviewRowIndex="setPreviewRowIndex"
        :fileName="fileName"
        :acceptedMimes="acceptedMimes"
        :validationError="validationError"
      />

      <massProducePreview
        v-if="stage === stages.PREVIEW"
        :storyboard="storyboard"
        :previewRow="previewRow"
      />

      <massProduceConfirm
        v-if="stage === stages.CONFIRM"
        :numRows="fileData.numRows"
      />

      <massProduceFinish
        v-if="stage === stages.FINISH"
        :produceState="produceState"
        :produceStates="produceStates"
        :produce="produce"
        :numRows="fileData.numRows"
        :storyboard="storyboard"
      />
    </div>

    <div class="-bottom">
      <div
        class="-mass-produce-storyboard-info wt2-text-n2"
        v-text="storyboard.title"
      />
      <div class="-actions">
        <wt-button
          type="transparent"
          :text="'Cancel'"
          :disabled="isProducing"
          v-show="!hasFinished"
          @click="closePrompt"
        ></wt-button>
        <wt-button
          type="primary"
          :disabled="!nextStage || isProducing"
          :text="nextStageActionLabel"
          @click="mainAction"
        ></wt-button>
      </div>
    </div>
  </div>
</template>

<script>
import * as Papa from 'papaparse';
import massProduceUpload from './components/wtMassProduce.upload.component.vue';
import massProduceBrowse from './components/wtMassProduce.browse.component.vue';
import massProducePreview from './components/wtMassProduce.preview.component.vue';
import massProduceConfirm from './components/wtMassProduce.confirm.component.vue';
import massProduceFinish from './components/wtMassProduce.finish.component.vue';

export default {
  components: {
    massProduceUpload,
    massProduceBrowse,
    massProducePreview,
    massProduceConfirm,
    massProduceFinish,
  },
  data() {
    return {
      showSpinner: false,
      stages: {
        UPLOAD: 'UPLOAD',
        BROWSE: 'BROWSE',
        PREVIEW: 'PREVIEW',
        CONFIRM: 'CONFIRM',
        FINISH: 'FINISH',
      },
      errors: {
        WRONG_FILE_FORMAT: 'WRONG_FILE_FORMAT',
        PARSE_ERROR: 'PARSE_ERROR',
      },
      produceStates: {
        UPLOAD: 'UPLOAD',
        SUCCESS: 'SUCCESS',
        FAILED: 'FAILED',
      },
      linkedFields: {},
      mandatoryLinkedFields: [],
      stage: 'UPLOAD',
      acceptedMimes: [
        'text/csv',
        'text/plain',
        'application/csv',
        'text/comma-separated-values',
        'application/excel',
        'application/vnd.ms-excel',
        'application/vnd.msexcel',
        'text/anytext',
        'application/octet-stream',
        'application/txt',
      ],
      file: null,
      fileName: null,
      fileValid: false,
      fileKey: null,
      fileData: {},
      fileError: null,
      previewRowIndex: 0,
      produceState: null,
    };
  },

  props: {
    storyboard: {
      type: Object,
      required: true,
    },
    closeModal: {
      type: Function,
      required: false,
    },
  },

  created() {
    this.showSpinner = true;
    app.api.renderApi
      .videoLinkedFields(this.storyboard.id)
      .then((res) => {
        const assetLinkedFields = _.keyBy(res.assetLinkedFields, 'name');
        const overlayLinkedFields = _.keyBy(res.overlayLinkedFields, 'name');
        this.linkedFields = Object.assign(
          this.linkedFields,
          assetLinkedFields,
          overlayLinkedFields
        );
        const mandatoryFields = _.filter(
          this.linkedFields,
          (l) => !!l.mandatory
        );
        this.mandatoryLinkedFields = _.map(mandatoryFields, 'name');
        this.showSpinner = false;
      })
      .catch((e) => {
        this.showSpinner = false;
        let error = 'Could not fetch storyboard data';
        if (e.noFields) {
          error = 'This storyboard is not compatible with mass produce';
        }
        app.dialog
          .error({
            type: app.dialog.types.ERROR_1,
            title: error,
            showContactCsm: true,
          })
          .then(() => {
            this.closePrompt();
          });
      });
  },

  mounted() {
    app.keyboardAccessibility.add('wt-mass-produce', [
      {
        on: 'keydown',
        key: $.ui.keyCode.ESCAPE,
        run: this.closePrompt,
      },
    ]);
  },

  destroyed() {
    app.keyboardAccessibility.remove('wt-mass-produce');
  },

  methods: {
    produce: _.debounce(
      function () {
        this.produceState = this.produceStates.UPLOAD;
        const channelBatchId = `${app.moment().format('YMD_HHmm')}`;
        const options = {
          channelBatchId,
          storyboardId: this.storyboard.id,
          videoRequestModels: [],
        };

        const dataLength = this.fileData.data.length;

        _.each(this.fileData.data, (row, index) => {
          const requestModel = {
            channelVideoId: `${row.___wochitKey} - ${channelBatchId}`,
            linkedFields: [],
            title:
              row._videoName ||
              `${channelBatchId}_${this.storyboard.title}_${
                index + 1
              }of${dataLength}`,
          };

          _.each(row, (value, key) => {
            if (this.linkedFields[key] !== undefined) {
              requestModel.linkedFields.push({
                key,
                value,
              });
            }
          });

          options.videoRequestModels.push(requestModel);
        });

        app.api.renderApi
          .postVideosBatch(options)
          .then((res) => {
            this.produceState = this.produceStates.SUCCESS;
          })
          .catch((e) => {
            this.produceState = this.produceStates.FAILED;
          });
      },
      app.TIME.MENTAL.λ,
      { leading: true, trailing: false }
    ),

    mainAction() {
      switch (this.stage) {
        case this.stages.CONFIRM:
          this.produce();
          this.setStage(this.nextStage);
          break;
        case this.stages.FINISH:
          this.closePrompt();
          break;
        default:
          this.setStage(this.nextStage);
          break;
      }
    },
    setStage(stage) {
      this.stage = stage;
    },
    setPreviewRowIndex(index) {
      this.previewRowIndex = index;
    },
    onFileChange(file) {
      // if (!this.acceptedMimes.includes(file['type'])) {
      //   return this.setFileError(this.errors.WRONG_FILE_FORMAT);
      // }
      this.showSpinner = true;
      Papa.parse(file, {
        header: true,
        skipEmptyLines: true,
        worker: true,

        complete: (parseResult) => {
          this.fileName = file.name;
          this.fileValid = false;
          this.previewRowIndex = 0;
          this.fileKey = Date.now();
          this.validateFileData(parseResult);
          this.stage = this.stages.BROWSE;
          this.showSpinner = false;
        },

        error: (err) => {
          this.setFileError(this.errors.PARSE_ERROR);
          this.showSpinner = false;
        },
      });
    },

    validateFileData(parseResult) {
      this.fileData = {
        fields: parseResult.meta.fields,
        duplicatedFields: [],
        missingFields: [],
        data: [],
        numRows: 0,
      };

      this.fileData.missingFields = _.differenceBy(
        this.mandatoryLinkedFields,
        parseResult.meta.fields
      );

      this.fileData.duplicatedFields = _.filter(
        parseResult.meta.fields,
        (val, i, iteratee) => _.includes(iteratee, val, i + 1)
      );

      _.each(parseResult.data, (res, index) => {
        this.fileData.data.push(
          Object.assign(res, {
            ___wochitKey: index,
          })
        );
        this.fileData.numRows++;
      });

      if (this.fileData.data.length) {
        this.fileValid = true;
      }
    },

    setFileError(error) {
      this.fileError = error;
    },

    clearFileError() {
      this.fileError = null;
    },

    closePrompt() {
      if (this.isProducing) {
        return;
      }
      if (!this.isDirty || this.hasFinished) {
        return this.closeModal();
      }
      return app.dialog
        .confirm({
          type: app.dialog.types.CONFIRM_2,
          title: 'Are you sure you want to exit?',
          buttonYesText: 'Exit',
          buttonNoText: app.culture.generic.CANCEL,
        })
        .then((data) => {
          if (data.action) {
            this.closeModal();
          }
        });
    },
  },

  computed: {
    isProducing() {
      return this.produceState === this.produceStates.UPLOAD;
    },

    hasFinished() {
      return this.produceState === this.produceStates.SUCCESS;
    },

    isDirty() {
      return this.fileKey;
    },

    nextStage() {
      switch (this.stage) {
        case this.stages.UPLOAD:
          return false;
        case this.stages.BROWSE:
          return this.fileValid ? this.stages.PREVIEW : false;
        case this.stages.PREVIEW:
          return this.stages.CONFIRM;
        case this.stages.CONFIRM:
          return this.stages.FINISH;
        case this.stages.FINISH:
          return true;
        default:
          return false;
      }
    },

    nextStageActionLabel() {
      switch (this.stage) {
        case this.stages.UPLOAD:
        case this.stages.BROWSE:
          return 'Create Sample Video';
        case this.stages.CONFIRM:
          return 'Start Mass Produce';
        case this.stages.FINISH:
          return 'Close';
        case this.stages.PREVIEW:
          return 'Like! Continue';
        default:
          return 'Continue';
      }
    },

    previewRow() {
      let row = {};
      if (this.fileValid) {
        row = this.fileData.data[this.previewRowIndex];
      }
      return row;
    },

    validationError() {
      let string = false;
      if (this.fileData.missingFields.length) {
        const length = this.fileData.missingFields.length;
        string =
          length > 1
            ? 'Required fields are missing: '
            : 'Required field is missing: ';
        string += this.fileData.missingFields.join(',');
        // string += '. Check and try again.';
      } else if (!this.fileData.data.length) {
        string = 'Error: Missing data.';
      } else if (this.fileData.duplicatedFields.length) {
        const length = this.fileData.duplicatedFields.length;
        string =
          length > 1
            ? 'There are duplicated columns: '
            : 'There’s a duplicate column: ';
        string += this.fileData.duplicatedFields.join(',');
        string += '. Check and try again.';
      }
      return string;
    },
  },
};
</script>

<style lang="scss" scoped>
@import 'style_guide/_sg_variables.scss';
.wt2-modal-content .wt-mass-produce {
  position: relative;
  display: flex;
  flex-direction: column;
  height: 610px;

  .close-icon {
    right: 21px;
    top: 21px;
    cursor: pointer;
    &.-disabled {
      pointer-events: none;
      opacity: 0.75;
    }
  }

  .-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    border-bottom: 1px solid #d9d9d9;
    height: 70px;
    padding: 20px 25px;
  }

  .-breadcrumbs {
    height: 50px;
    padding-right: 40px;
    display: flex;
    justify-content: center;
    align-items: center;
    border-bottom: 1px solid #d9d9d9;

    > div {
      color: #d9d9d9;
      display: flex;
      flex-direction: column;
      justify-content: center;
      position: relative;
      height: 50px;
      width: 188px;
      padding: 0 30px;
      pointer-events: none;

      &.-enabled {
        &:not(.-active) {
          cursor: pointer;
          pointer-events: all;
          color: rgba(0, 0, 0, 0.75);
        }
      }

      &:not(.-active):after {
        content: '';
        display: block;
        position: absolute;
        height: 100%;
        width: 11px;
        top: 0;
        right: -11px;
        background: url('/resources/img/sep2.png') no-repeat;
      }
      i {
        padding: 0 5px;
      }
      &.-active {
        background-color: $color-primary;
        color: $color-negative;
        &:after {
          content: '';
          display: block;
          position: absolute;
          top: 0;
          right: -20px;
          height: 100%;
          border-top: transparent 25px solid;
          border-right: transparent 10px solid;
          border-bottom: transparent 25px solid;
          border-left: $color-primary 10px solid;
        }
        &:before {
          content: '';
          display: block;
          position: absolute;
          top: 0;
          left: 0px;
          width: 10px;
          height: 100%;
          border-top: $color-primary 25px solid;
          border-right: $color-primary 10px solid;
          border-bottom: $color-primary 25px solid;
          border-left: $color-white 10px solid;
        }
        &:first-child {
          &:before {
            content: '';
            display: none;
          }
        }
      }
    }
  }

  .-stage {
    padding: 20px;
    flex-grow: 1;
    position: relative;

    .-spinner {
      position: absolute;
      width: 100%;
      height: 100%;
      background: rgba(255, 255, 255, 0.85);
      left: 0;
      top: 0;
      z-index: 1;
      display: flex;
      align-items: center;
      justify-content: center;
      .wt-loader {
        transform: scale(0.35) translate3d(0, 0, 0);
      }
    }
  }

  .-bottom {
    height: 50px;
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    position: relative;
    border-top: 1px solid #d9d9d9;
    padding: 0;
    padding-left: 20px;

    .-mass-produce-storyboard-info {
      width: 350px;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    .-actions {
      display: flex;
    }

    button.primary {
      width: 200px;
    }
  }
}
</style>
>
